import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Inject,
    input,
    OnDestroy,
    Renderer2,
} from '@angular/core';
import { OverlaysService, POSITION_TARGET, PositionTarget } from 'app/shared/ui/overlays';
import { DropdownDirective } from './dropdown.directive';


@Component({
    selector: 'ui-dropdown-menu',
    host: {
        'animate.enter': 'fade-enter',
        'animate.leave': 'fade-leave',
        'style': '--fade-duration: 0.2s',
        'class': 'shadow',
        'role': 'menu',
    },
    styles: [
        `
        :host {
            display: block;
            position: absolute;
            z-index: 1000;
            min-width: 10rem;
            padding: 0;
            margin: 0;
            color: #1D2531;
            background: #FFFFFF;
            background-clip: padding-box;
            border: 1px solid rgba(0, 0, 0, 0.15);
            border-radius: 0.25rem;
            overflow-y: auto;
        }
        :host ::ng-deep .dropdown-item {
            display: block;
            width: 100%;
            padding: 0.5rem 0.75rem;
            color: #212529;
            white-space: nowrap;
            background: white;
            background-clip: border-box;
            border: 0;
        }
        :host ::ng-deep .dropdown-item:first-child {
            border-top-left-radius: 0.25rem;
            border-top-right-radius: 0.25rem;
        }
        :host ::ng-deep .dropdown-item:last-child {
            border-bottom-left-radius: 0.25rem;
            border-bottom-right-radius: 0.25rem;
        }
        :host ::ng-deep .dropdown-item:active,
        :host ::ng-deep .dropdown-item.active {
            color: #1583D6;
            background: #F1F4F8;
        }
        :host ::ng-deep .dropdown-item.disabled,
        :host ::ng-deep button.dropdown-item:disabled {
            color: #6C757D;
            background: #F5F5F5;
        }
        :host ::ng-deep button.dropdown-item:disabled {
            cursor: default;
        }

        :host ::ng-deep .dropdown-item:not(.disabled):not([disabled]):not(.loading):hover {
            color: #1583D6;
            background: #EBEBEB;
        }

        :host ::ng-deep button.dropdown-item {
            cursor: pointer;
            outline: none;
            text-align: left;
        }
        :host ::ng-deep a.dropdown-item:hover {
            text-decoration: none;
        }
        `,
    ],
    template: `
        <ng-content></ng-content>
    `,
})
export class DropdownMenuComponent implements AfterViewInit, OnDestroy {

    placement = input<'left' | 'right'>('left');

    private animationFrame: number;

    constructor(
        private dropdown: DropdownDirective,
        private renderer: Renderer2,
        private elementRef: ElementRef,
        private overlaysService: OverlaysService,
        @Inject(POSITION_TARGET) private positionTarget: PositionTarget,
    ) {}

    ngAfterViewInit() {
        this.startCalculatingPosition();
    }

    private startCalculatingPosition() {
        this.calcPosition();
        this.animationFrame = requestAnimationFrame(() => this.startCalculatingPosition());
    }

    private calcPosition() {
        const el = this.elementRef.nativeElement as HTMLElement;
        const viewRect = this.overlaysService.getBoundingClientRect();
        const hostRect = this.positionTarget.getBoundingClientRect();

        let newLeft: number;
        let newTop: number;

        // keep menu bottom. If does not fit bottom and fits on top, place on top.
        newTop = hostRect.bottom;
        if ((newTop + el.offsetHeight) > viewRect.bottom && (hostRect.top - el.offsetHeight) >= viewRect.top) {
            newTop = hostRect.top - el.offsetHeight;
        }

        const maxHeight = Math.min(el.scrollHeight + 10, viewRect.height - newTop - 10);

        switch (this.placement()) {
            case 'right': {
                newLeft = hostRect.right - el.offsetWidth;
                if (newLeft < viewRect.left) {
                    newLeft = hostRect.left;
                }
                break;
            }
            case 'left':
            default: {
                newLeft = hostRect.left;
                if (newLeft + el.offsetWidth > viewRect.right) {
                    newLeft = hostRect.right - el.offsetWidth;
                }
            }
        }
        this.renderer.setStyle(el, 'left', `${newLeft}px`);
        this.renderer.setStyle(el, 'top', `${newTop}px`);
        this.renderer.setStyle(el, 'max-height', `${maxHeight}px`);
    }

    @HostListener('click', ['$event'])
    onClick(event: Event) {
        const tagName = (event.target as HTMLElement | null)?.tagName;
        event.stopImmediatePropagation();
        if (tagName === 'A' || tagName === 'BUTTON') {
            this.dropdown.isOpen.set(false);
        }
    }

    ngOnDestroy() {
        cancelAnimationFrame(this.animationFrame);
    }
}
